/**
 * Route inventory — the single source of truth for every public URL.
 *
 * Framing V9 (`.i/info/market/261001-visibility-audit.md`): one inventory
 * drives client routing, the build-time prerender, the sitemap, llms.txt
 * and the IndexNow delta. Add a public page here and every surface follows.
 *
 * Rules
 * - Paths are canonical: lowercase, singular, no trailing slash.
 * - `updated` is the last material content change to that page (YYYY-MM-DD).
 *   It feeds the sitemap `lastmod`; bump it when the page copy changes.
 * - App-only paths are never prerendered, never in the sitemap, and are
 *   served with `noindex` from the bare shell (`dist/app.html`).
 */

export const SITE_URL = 'https://sigtdd.com';
export const SITE_NAME = 'SigTDD';
export const CONTACT_EMAIL = 'admin@sigtdd.com';

export type PublicKind =
  | 'landing'
  | 'features'
  | 'pricing'
  | 'docs'
  | 'contact'
  | 'learn';

export interface LearnMeta {
  /** Short label used in navigation and breadcrumbs */
  label: string;
  /** Target search queries fixed in framing V10 */
  query: string[];
  /** One-line summary used by llms.txt and internal link cards */
  summary: string;
  /** First publication date (ISO) for TechArticle JSON-LD */
  published: string;
}

export interface PublicRoute {
  path: string;
  kind: PublicKind;
  /** `<title>` — unique per route */
  title: string;
  /** meta description — unique per route, ≤ 160 characters */
  description: string;
  /** Short heading used in breadcrumbs and cards */
  label: string;
  /** Last material content change, ISO date; sitemap lastmod */
  updated: string;
  learn?: LearnMeta;
}

/** Slug used for the per-route social card `/og/<slug>.png` */
export function routeSlug(path: string): string {
  return path === '/' ? 'home' : path.slice(1).replace(/\//g, '-');
}

export function ogImagePath(path: string): string {
  return `/og/${routeSlug(path)}.png`;
}

export const PUBLIC_ROUTE_LIST: readonly PublicRoute[] = [
  {
    path: '/',
    kind: 'landing',
    label: 'Home',
    title: 'SigTDD — Free online time-distance diagram tool for traffic signal coordination',
    description:
      'Draw time-distance (time-space) diagrams for traffic signal coordination in the browser. Import a corridor, set offsets, watch the green wave form, share a link. Free tier, no installation.',
    updated: '2026-10-01',
  },
  {
    path: '/features',
    kind: 'features',
    label: 'Features',
    title: 'Features — SigTDD time-distance diagram tool',
    description:
      'What SigTDD does: CSV, JSON and Synchro UTDF import, multiple timing plans per corridor, auto-sequence offsets, progression bands, share links, PDF and CSV export.',
    updated: '2026-10-01',
  },
  {
    path: '/pricing',
    kind: 'pricing',
    label: 'Pricing',
    title: 'Pricing — SigTDD free and Pro tiers',
    description:
      'SigTDD is free for evaluation and small corridors. Pro adds larger corridors, more timing plans, PDF export and Synchro UTDF import for a flat monthly price in AUD.',
    updated: '2026-10-01',
  },
  {
    path: '/docs',
    kind: 'docs',
    label: 'Docs',
    title: 'Documentation — SigTDD user guide',
    description:
      'SigTDD user guide: what a time-distance diagram is, getting started, timing plans, reading the diagram, data entry, import and export formats, settings and FAQ.',
    updated: '2026-10-01',
  },
  {
    path: '/contact',
    kind: 'contact',
    label: 'Contact',
    title: 'Contact — SigTDD',
    description:
      'Contact SigTDD about the time-distance diagram tool: support, pricing, feature requests and bug reports.',
    updated: '2026-10-01',
  },
  {
    path: '/learn/time-distance-diagram',
    kind: 'learn',
    label: 'What is a time-distance diagram?',
    title: 'What is a time-distance diagram (time-space diagram) for traffic signals?',
    description:
      'A time-distance diagram, also called a time-space diagram, plots traffic signal phases against distance along a corridor so engineers can see and tune progression.',
    updated: '2026-10-01',
    learn: {
      label: 'Time-distance diagram',
      query: ['what is a time-distance diagram', 'time-space diagram traffic signals'],
      summary:
        'Definition, axes, phase bars, trajectories and bandwidth, with a worked diagram from a real corridor.',
      published: '2026-10-01',
    },
  },
  {
    path: '/learn/green-wave-offset',
    kind: 'learn',
    label: 'How to set signal offsets for a green wave',
    title: 'How to set traffic signal offsets for a green wave',
    description:
      'Offset calculation for a green wave: travel time from distance and design speed, cumulative offsets, cycle length, and the two-way progression trade-off, shown on a time-distance diagram.',
    updated: '2026-10-01',
    learn: {
      label: 'Green wave offsets',
      query: ['green wave signal offset', 'signal offset calculation'],
      summary:
        'The offset formula, cumulative offsets along a corridor, and what limits two-way progression.',
      published: '2026-10-01',
    },
  },
  {
    path: '/learn/worked-example-corridor',
    kind: 'learn',
    label: 'Worked example: a six-intersection corridor',
    title: 'Worked example: coordinating a six-intersection traffic signal corridor',
    description:
      'Step-by-step coordination of the Riverside Drive example corridor: CSV input, travel times, offsets, the resulting northbound green wave and its bandwidth on a time-distance (time-space) diagram.',
    updated: '2026-10-01',
    learn: {
      label: 'Worked example corridor',
      query: ['traffic signal coordination example'],
      summary:
        'Every number for the shipped NB-Green-Wave.csv corridor, from spacing and speed to offsets and bandwidth.',
      published: '2026-10-01',
    },
  },
  {
    path: '/learn/synchro-utdf-import',
    kind: 'learn',
    label: 'Importing Synchro UTDF timing',
    title: 'Importing Synchro UTDF timing into a time-distance diagram',
    description:
      'How to export a UTDF file from Synchro and import it into SigTDD: which sections are read, how node coordinates become corridor distances, and what to check after import.',
    updated: '2026-10-01',
    learn: {
      label: 'Synchro UTDF import',
      query: ['Synchro UTDF import', 'UTDF time-space diagram'],
      summary:
        'UTDF sections read, field mapping to corridor distance, offsets and phases, and the post-import checklist.',
      published: '2026-10-01',
    },
  },
];

export const LEARN_ROUTE_LIST = PUBLIC_ROUTE_LIST.filter((r) => r.kind === 'learn');

/** Client-only pages: served from the bare shell, `noindex`, never in the sitemap */
export type AppPage =
  | 'app'
  | 'login'
  | 'signup'
  | 'verify-email'
  | 'forgot-password'
  | 'reset-password';

const APP_PAGE_BY_PATH: Record<string, AppPage> = {
  '/app': 'app',
  '/login': 'login',
  '/signup': 'signup',
  '/verify-email': 'verify-email',
  '/forgot-password': 'forgot-password',
  '/reset-password': 'reset-password',
};

/** Paths Caddy must serve from the app shell (mirrors `caddy/sigtdd.caddy`) */
export const APP_PATH_LIST = Object.keys(APP_PAGE_BY_PATH);

export type RouteMatch =
  | { kind: 'public'; route: PublicRoute }
  | { kind: 'app'; page: AppPage }
  | { kind: 'share'; token: string }
  | { kind: 'not-found' };

/** Canonical form: strip trailing slash, lowercase the path */
export function normalizePath(pathname: string): string {
  let p = pathname || '/';
  if (p.length > 1 && p.endsWith('/')) p = p.replace(/\/+$/, '');
  return p || '/';
}

const publicByPath = new Map(PUBLIC_ROUTE_LIST.map((r) => [r.path, r]));

export function matchRoute(pathname: string): RouteMatch {
  const path = normalizePath(pathname);
  const route = publicByPath.get(path);
  if (route) return { kind: 'public', route };
  const page = APP_PAGE_BY_PATH[path];
  if (page) return { kind: 'app', page };
  const share = path.match(/^\/share\/([a-f0-9]+)$/i);
  if (share) return { kind: 'share', token: share[1] };
  return { kind: 'not-found' };
}

export function absoluteUrl(path: string): string {
  return path === '/' ? `${SITE_URL}/` : `${SITE_URL}${path}`;
}
